/**
 * Public articles (Phase 25): staff management endpoints. The public site
 * reads published articles server-side through `lib/publicArticles.ts`.
 */

import { fetchApi } from '../core';
import { objectIdPath } from '../path';
import type {
  Article,
  ArticleCategory,
  ArticleSummary,
  CreateArticleData,
  LocalizedName,
  UpdateArticleData,
} from '../types';

const articlePath = (id: string) => `/articles/${objectIdPath(id, 'article')}`;
const categoryPath = (id: string) => `/article-categories/${objectIdPath(id, 'article category')}`;

export function list(componentName: string = 'Unknown') {
  return fetchApi<ArticleSummary[]>('/articles', {}, componentName);
}

export function get(id: string, componentName: string = 'Unknown') {
  return fetchApi<Article>(articlePath(id), {}, componentName);
}

export function create(data: CreateArticleData, componentName: string = 'Unknown') {
  return fetchApi<Article>('/articles', { method: 'POST', body: JSON.stringify(data) }, componentName);
}

export function update(id: string, data: UpdateArticleData, componentName: string = 'Unknown') {
  return fetchApi<Article>(articlePath(id), { method: 'PUT', body: JSON.stringify(data) }, componentName);
}

export function publish(
  id: string,
  expectedUpdatedAt: number,
  componentName: string = 'Unknown'
) {
  return fetchApi<Article>(
    `${articlePath(id)}/publish`,
    { method: 'POST', body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }) },
    componentName
  );
}

export function unpublish(
  id: string,
  expectedUpdatedAt: number,
  componentName: string = 'Unknown'
) {
  return fetchApi<Article>(
    `${articlePath(id)}/unpublish`,
    { method: 'POST', body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }) },
    componentName
  );
}

export function remove(id: string, componentName: string = 'Unknown') {
  return fetchApi<null>(articlePath(id), { method: 'DELETE' }, componentName);
}

export function listCategories(componentName: string = 'Unknown') {
  return fetchApi<ArticleCategory[]>('/article-categories', {}, componentName);
}

export function createCategory(
  data: { name: LocalizedName; sort_order?: number },
  componentName: string = 'Unknown'
) {
  return fetchApi<ArticleCategory>(
    '/article-categories',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateCategory(
  id: string,
  data: { expected_updated_at: number; name?: LocalizedName; sort_order?: number },
  componentName: string = 'Unknown'
) {
  return fetchApi<ArticleCategory>(
    categoryPath(id),
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function removeCategory(id: string, componentName: string = 'Unknown') {
  return fetchApi<null>(categoryPath(id), { method: 'DELETE' }, componentName);
}
